import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { BaseComponent } from '@shared/components/base-component';
import { ChitinIconComponent } from '@shared/components/chitin/atoms/icon/chitin-icon.component';
import { LoadingSpinnerComponent } from '@shared/components/loading-spinner/loading-spinner.component';
import { SelectListItem } from '@shared/models/select-list-item';

type OptionGroup = {
  open: boolean;
  selected: boolean;
  items: SelectListItem[];
};

@Component({
  selector: 'chitin-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [{ provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => ChitinSelectComponent) }],
  imports: [CommonModule, ChitinIconComponent, FormsModule, NgbDropdownModule, LoadingSpinnerComponent],
})
export class ChitinSelectComponent<TValue, TItem extends TValue | TValue[] | undefined> extends BaseComponent implements AfterViewInit {
  private readonly cdr = inject(ChangeDetectorRef);

  isLoading = input(false);
  value = model<TItem>();
  options = input<Array<SelectListItem<TValue>>>([]);
  disabled = input(false);
  searchable = input(false);
  placeholder = input('Select option');
  size = input<'sm' | 'md' | 'lg'>('md');
  groupItems = input(false);
  openGroupsByDefault = input(false);
  type = input<'input' | 'button'>('input');
  dropdownClass = input<string>();
  inputClass = input<string>('');
  appendTo = input<'body' | 'parent'>('body');
  displaySelectedItems = input(false);
  inputClassCombined = computed(() => `form-control-${this.size()} ${this.inputClass()}`);

  selectedItemChange = output<SelectListItem<TValue>>();

  selectDropdown = viewChild<ElementRef>('#selectDropdown');
  multiple = false;
  searchTerm?: string;
  visibleOptions: Array<SelectListItem<TValue>> = [];
  selectedItems: Array<SelectListItem<TValue>> = [];
  groupedItems: Record<string, OptionGroup>;
  displayText: string = '';

  constructor() {
    super();
    effect(() => {
      this.multiple = Array.isArray(this.value());
      this.updateSelectedItems();
      this.updateDisplayText();
      this.cdr.markForCheck();
    });
    effect(() => {
      this.updateVisibleItems();
      if (this.groupItems()) {
        this.updateGroupedItems();
      }
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit() {
    this.updateVisibleItems();
    this.updateDisplayText();
    this.updateSelectedItems();
    if (this.groupItems()) {
      this.updateGroupedItems();
    }
    this.cdr.markForCheck();
  }

  focus() {
    this.selectDropdown()?.nativeElement?.focus();
  }

  onSearchTermChanged(searchTerm?: string) {
    this.searchTerm = searchTerm;
    this.updateVisibleItems();
  }

  selectItem(item: SelectListItem<TValue>) {
    const currentValue = this.value();
    if (Array.isArray(currentValue)) {
      this.value.update(v => [...(v as TValue[]), item.value] as TItem);
      const existingIndex = this.selectedItems.findIndex(i => i.value === item.value);
      if (existingIndex !== -1) {
        this.selectedItems.splice(existingIndex, 1);
      } else {
        this.selectedItems.push(item);
      }
    } else {
      this.value.set(item.value as TItem);
      this.selectedItems = [item];
    }
    this.updateDisplayText();
    this.onSearchTermChanged('');
  }

  updateVisibleItems() {
    const searchTerm = this.searchTerm?.toLowerCase();
    this.visibleOptions = searchTerm ? this.options().filter(item => item.name.toLowerCase().includes(searchTerm)) : this.options();
    if (this.groupItems()) {
      this.updateGroupedItems();
    }
  }

  updateDisplayText() {
    if (this.selectedItems.length > 1) {
      if (this.displaySelectedItems()) {
        this.displayText = this.selectedItems.map(item => item.name).join(', ');
      } else {
        this.displayText = `${this.selectedItems.length} selected`;
      }
    } else if (this.selectedItems.length > 0) {
      this.displayText = this.selectedItems[0].name;
    } else {
      this.displayText = this.placeholder();
    }
  }

  updateSelectedItems() {
    this.selectedItems = [];
    const value = this.value();
    if (Array.isArray(value)) {
      for (const v of value) {
        const item = this.options().find(option => option.value === v);
        if (item) this.selectedItems.push(item);
      }
    } else {
      const item = this.options().find(option => option.value === value);
      if (item) this.selectedItems = [item];
    }
  }

  updateGroupedItems() {
    this.groupedItems = {};
    for (const option of this.visibleOptions) {
      if (option.group) {
        if (Object.keys(this.groupedItems).includes(option.group)) {
          this.groupedItems[option.group].items.push(option);
        } else {
          this.groupedItems[option.group] = { selected: false, open: this.openGroupsByDefault() || !!this.searchTerm, items: [option] };
        }
      } else {
        if (Object.keys(this.groupedItems).includes('Other')) {
          this.groupedItems['Other'].items.push(option);
        } else {
          this.groupedItems['Other'] = { selected: false, open: this.openGroupsByDefault() || !!this.searchTerm, items: [option] };
        }
      }
    }
  }
}
